<template>
  <main class="service-detail-page">
    <section v-if="loading" class="detail-shell detail-loading" aria-label="A carregar detalhes">
      <div class="loading-gallery" />
      <div class="loading-grid">
        <div class="loading-panel" />
        <div class="loading-panel loading-panel--side" />
      </div>
    </section>

    <section v-else-if="error" class="detail-shell detail-error" role="alert">
      <q-icon name="error_outline" size="48px" />
      <h1>{{ error }}</h1>
      <p>Verifique a ligação e tente novamente.</p>
      <button type="button" @click="load">Tentar novamente</button>
    </section>

    <template v-else>
      <nav class="detail-shell breadcrumbs" aria-label="Navegação estrutural">
        <button type="button" @click="goBack">{{ copy.section }}</button>
        <span aria-hidden="true">/</span>
        <span>{{ copy.title }}</span>
      </nav>

      <section
        class="detail-shell gallery-section"
        :class="{ 'gallery-section--single': thumbnailImages.length === 0 }"
        :aria-label="`Galeria de ${copy.title}`"
      >
        <button class="gallery-main" type="button" @click="openGallery(currentImage)">
          <img :src="mainImage" :alt="copy.title" />
          <span class="gallery-badges">
            <span v-if="promotionLabel" class="promotion-badge">{{ promotionLabel }}</span>
            <span v-for="tag in copy.tags.slice(0, 2)" :key="tag">{{ tag }}</span>
          </span>
          <span v-if="images.length > 1" class="gallery-arrows" aria-hidden="true">
            <span @click.stop="previousImage">‹</span>
            <span @click.stop="nextImage">›</span>
          </span>
        </button>

        <div
          v-if="thumbnailImages.length"
          class="gallery-thumbnails"
          :class="`gallery-thumbnails--${thumbnailImages.length}`"
        >
          <button
            v-for="(image, index) in thumbnailImages"
            :key="`${image}-${index}`"
            type="button"
            :class="{ 'is-active': currentImage === index + 1 }"
            @click="selectImage(index + 1)"
          >
            <img :src="image" :alt="`${copy.title} — fotografia ${index + 2}`" />
            <span v-if="index === thumbnailImages.length - 1 && images.length > 5" class="gallery-more">
              Ver todas ({{ images.length }})
            </span>
          </button>
        </div>
      </section>

      <section class="detail-shell detail-content">
        <div class="detail-primary">
          <header class="service-heading">
            <div class="service-heading__badges">
              <span v-if="promotionLabel" class="promotion-badge">{{ promotionLabel }}</span>
              <span v-for="tag in copy.tags" :key="tag">{{ tag }}</span>
            </div>
            <h1>{{ copy.title }}</h1>
            <p class="service-location">
              <q-icon name="location_on" size="18px" />
              {{ copy.subtitle }}
            </p>
            <p class="service-price">
              {{ copy.price }}<small>{{ copy.priceUnit }}</small>
            </p>
          </header>

          <section class="detail-block characteristics-section">
            <h2>Características</h2>
            <dl class="characteristics-grid">
              <div v-for="row in characteristicRows" :key="row.label">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="!isRent && units.length" class="detail-block option-section">
            <div class="section-heading">
              <div>
                <span>ESCOLHA A UNIDADE</span>
                <h2>Opções disponíveis</h2>
              </div>
              <small>{{ units.length }} {{ units.length === 1 ? 'unidade' : 'unidades' }}</small>
            </div>
            <div class="option-grid">
              <button
                v-for="unit in units"
                :key="unit.id"
                type="button"
                :class="{ 'is-selected': String(booking.option) === String(unit.id) }"
                @click="booking.option = unit.id"
              >
                <span class="option-icon"><q-icon name="hotel" size="21px" /></span>
                <span class="option-copy"><strong>{{ unit.name }}</strong><small>{{ unit.capacity }} hóspedes · {{ unit.bed_amount || 0 }} camas</small></span>
                <span class="option-price">{{ formatPrice(unit.price, unit.currency) }}<small>/noite</small></span>
                <q-icon class="option-check" name="check_circle" size="20px" />
              </button>
            </div>
          </section>

          <section v-if="isRent && serviceOptions.length" class="detail-block option-section">
            <div class="section-heading">
              <div>
                <span>MODALIDADE DE ALUGUER</span>
                <h2>Escolha como quer viajar</h2>
              </div>
            </div>
            <div class="option-grid option-grid--services">
              <button
                v-for="service in serviceOptions"
                :key="service.service_type"
                type="button"
                :class="{ 'is-selected': booking.serviceType === service.service_type }"
                @click="booking.serviceType = service.service_type"
              >
                <span class="option-icon"><q-icon :name="service.service_type === 'with_driver' ? 'person_pin_circle' : 'key'" size="21px" /></span>
                <span class="option-copy">
                  <strong>{{ serviceLabel(service.service_type) }}</strong>
                  <small>{{ service.service_type === 'with_driver' ? 'Motorista profissional incluído' : 'Conduza ao seu ritmo' }}</small>
                </span>
                <span class="option-price">{{ formatPrice(service.price ?? item.terms?.daily_price, item.terms?.currency) }}<small>/dia</small></span>
                <q-icon class="option-check" name="check_circle" size="20px" />
              </button>
            </div>
          </section>

          <section v-if="amenities.length" class="detail-block amenities-section">
            <h2>Comodidades</h2>
            <div class="amenities-grid">
              <div v-for="amenity in amenities" :key="amenity">
                <q-icon name="check" size="17px" />
                <span>{{ amenity }}</span>
              </div>
            </div>
          </section>

          <section v-if="isRent" class="detail-block conditions-section">
            <h2>Condições de aluguer</h2>
            <dl class="characteristics-grid">
              <div v-for="row in rentalConditions" :key="row.label">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="copy.description" class="detail-block description-section">
            <h2>Descrição</h2>
            <p>{{ copy.description }}</p>
          </section>

          <section v-if="item.location" class="detail-block location-section">
            <h2>Localização</h2>
            <div class="location-placeholder">
              <q-icon name="location_on" size="25px" />
              <strong>{{ item.location }}</strong>
              <span>{{ isRent ? 'Confirme o local de levantamento com o agente responsável.' : 'A localização exacta é confirmada pelo agente responsável.' }}</span>
            </div>
          </section>
        </div>

        <aside class="detail-sidebar">
          <section class="contact-card">
            <div class="agent-row">
              <img :src="agent.photo || fallbackImage" :alt="agent.name || 'Agente responsável'" />
              <div>
                <span>Agente responsável</span>
                <strong>{{ agent.name || 'Golden Key' }}</strong>
                <small v-if="agent.email">{{ agent.email }}</small>
              </div>
            </div>

            <a v-if="agent.phone" class="contact-button contact-button--primary" :href="`tel:${agent.phone}`">Ligar: {{ agent.phone }}</a>
            <a v-if="agent.whatsapp" class="contact-button" :href="whatsAppUrl" target="_blank" rel="noopener noreferrer">WhatsApp</a>
            <button class="contact-button contact-button--save" type="button" @click="toggleFavorite">
              <q-icon :name="favorite ? 'favorite' : 'favorite_border'" size="18px" />
              {{ favorite ? 'Guardado nos favoritos' : 'Guardar nos favoritos' }}
            </button>

            <dl class="contact-meta">
              <div><dt>Categoria</dt><dd>{{ isRent ? item.category?.name || 'Viatura' : item.type?.name || 'Alojamento' }}</dd></div>
              <div><dt>Preço</dt><dd>{{ copy.price }}{{ copy.priceUnit }}</dd></div>
              <div v-if="isRent"><dt>Serviço</dt><dd>{{ serviceOptions.map(option => serviceLabel(option.service_type)).join(' / ') || 'Sob consulta' }}</dd></div>
              <div v-else><dt>Unidades</dt><dd>{{ units.length || 'Sob consulta' }}</dd></div>
            </dl>
          </section>

          <section class="partner-section">
            <span>GOLDEN KEY</span>
            <h2>Encontre a opção certa para si</h2>
            <CarouselAd class="detail-partner-ad" variant="sidebar" placement-key="catalog_detail_sidebar" />
          </section>
        </aside>
      </section>

      <section v-if="relatedLoading || relatedItems.length" class="detail-shell related-section">
        <div class="related-heading">
          <h2>{{ isRent ? 'Viaturas semelhantes' : 'Alojamentos semelhantes' }}</h2>
          <div class="related-heading__actions">
            <div class="related-slider-controls" :aria-label="`Navegar por ${isRent ? 'viaturas' : 'alojamentos'} semelhantes`">
              <button type="button" aria-label="Itens anteriores" @click.stop="scrollRelated(-1)">←</button>
              <button type="button" aria-label="Próximos itens" @click.stop="scrollRelated(1)">→</button>
            </div>
            <button class="related-see-all" type="button" @click="router.push(backPath)">Ver todos →</button>
          </div>
        </div>
        <div v-if="relatedLoading" class="related-grid related-grid--loading"><div v-for="n in 4" :key="n" /></div>
        <div v-else ref="relatedTrack" class="related-grid related-grid--carousel" :data-related-index="relatedIndex">
          <article v-for="related in relatedItems" :key="related.slug" tabindex="0" @click="openRelated(related)" @keydown.enter="openRelated(related)">
            <div class="related-image">
              <img :src="relatedImage(related)" :alt="related.title" loading="lazy" />
              <span>{{ isRent ? related.category?.name : related.type?.name }}</span>
            </div>
            <div class="related-content">
              <strong>{{ relatedPrice(related) }}<small>{{ isRent ? '/dia' : '/noite' }}</small></strong>
              <h3>{{ related.title }}</h3>
              <p>{{ related.location || 'Localização sob consulta' }}</p>
              <div class="related-facts">
                <span v-if="isRent && related.transmission">{{ transmissionLabel(related.transmission) }}</span>
                <span v-if="isRent && related.seats">{{ related.seats }} lugares</span>
                <span v-if="!isRent && related.capacity">Até {{ related.capacity }} hóspedes</span>
                <span v-if="!isRent && related.bedroom_amount">{{ related.bedroom_amount }} quartos</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <q-dialog v-model="galleryOpen" maximized>
        <q-card class="gallery-dialog bg-black">
          <q-bar class="bg-transparent">
            <q-space />
            <q-btn flat dense icon="close" color="white" v-close-popup aria-label="Fechar galeria" />
          </q-bar>
          <q-card-section class="gallery-dialog__content">
            <q-carousel v-model="currentImage" animated navigation infinite arrows control-color="primary" class="gallery-carousel">
              <q-carousel-slide v-for="(image, index) in images" :key="`${image}-${index}`" :name="index" :img-src="image" />
            </q-carousel>
          </q-card-section>
        </q-card>
      </q-dialog>
    </template>
  </main>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '@/config/api'
import fallbackImage from '@/assets/logo.png'
import CarouselAd from './CarouselAd.vue'

const props = defineProps({ kind: { type: String, default: 'accommodation' } })
const route = useRoute()
const router = useRouter()
const item = ref({})
const loading = ref(true)
const error = ref('')
const favorite = ref(false)
const currentImage = ref(0)
const galleryOpen = ref(false)
const booking = reactive({ option: '', serviceType: '' })
const relatedItems = ref([])
const relatedLoading = ref(false)
const relatedTrack = ref(null)
const relatedIndex = ref(0)

const isRent = computed(() => props.kind === 'rent')
const backPath = computed(() => isRent.value ? '/rent-a-car' : '/alojamentos')
const units = computed(() => (item.value.units || []).filter((unit) => unit.active !== false && Number(unit.active) !== 0))
const serviceOptions = computed(() => (item.value.service_options || []).filter((service) => service.active !== false && Number(service.active) !== 0))
const selectedUnit = computed(() => units.value.find((unit) => String(unit.id) === String(booking.option)) || units.value[0])
const selectedService = computed(() => serviceOptions.value.find((service) => service.service_type === booking.serviceType) || serviceOptions.value[0])
const agent = computed(() => item.value.user || {})
const whatsAppUrl = computed(() => {
  const number = String(agent.value.whatsapp || '').replace(/\D/g, '')
  const message = encodeURIComponent(`Olá! Tenho interesse em ${copy.value.title}.`)
  return `https://wa.me/${number}?text=${message}`
})
const promotionLabel = computed(() => item.value.promotion?.badge_label
  || (item.value.promotion?.editorial_featured ? 'Destaque editorial' : ''))
const images = computed(() => {
  const candidates = [item.value.primary_image, ...(item.value.images || [])]
    .map((image) => typeof image === 'string' ? { url: image, is_primary: false } : image)
    .filter((image) => image?.url)
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
    .map((image) => image.url)
  const unique = [...new Set(candidates)]
  return unique.length ? unique : [fallbackImage]
})
const mainImage = computed(() => images.value[currentImage.value] || images.value[0])
const thumbnailImages = computed(() => images.value.slice(1, 5))
const amenities = computed(() => (item.value.amenities || []).map((amenity) => typeof amenity === 'string' ? amenity : amenity.name).filter(Boolean))
const copy = computed(() => {
  if (isRent.value) {
    return {
      section: 'Rent-a-car',
      title: item.value.title || 'Viatura',
      subtitle: [item.value.location, item.value.brand, item.value.model].filter(Boolean).join(' · '),
      tags: [item.value.category?.name, ...serviceOptions.value.map((service) => serviceLabel(service.service_type))].filter(Boolean),
      price: formatPrice(selectedService.value?.price ?? item.value.terms?.daily_price, item.value.terms?.currency),
      priceUnit: '/dia',
      description: item.value.description,
    }
  }
  return {
    section: 'Alojamentos',
    title: item.value.title || 'Alojamento',
    subtitle: [item.value.location, item.value.type?.name].filter(Boolean).join(' · '),
    tags: [item.value.type?.name].filter(Boolean),
    price: formatPrice(selectedUnit.value?.price ?? item.value.price, selectedUnit.value?.currency ?? item.value.currency),
    priceUnit: '/noite',
    description: item.value.description,
  }
})

const characteristicRows = computed(() => {
  const rows = []
  const add = (label, value) => {
    if (value !== undefined && value !== null && value !== '') rows.push({ label, value })
  }
  if (isRent.value) {
    add('Categoria', item.value.category?.name)
    add('Marca', item.value.brand)
    add('Modelo', item.value.model)
    add('Ano', item.value.year)
    add('Transmissão', transmissionLabel(item.value.transmission))
    add('Combustível', fuelLabel(item.value.fuel))
    add('Lugares', item.value.seats)
    add('Capacidade de bagagem', item.value.luggage_capacity != null ? `${item.value.luggage_capacity} malas` : '')
    add('Ar condicionado', item.value.air_conditioning ? 'Sim' : 'Não')
  } else {
    const unit = selectedUnit.value || {}
    add('Tipo de alojamento', item.value.type?.name)
    add('Unidade', unit.name)
    add('Capacidade', unit.capacity != null ? `${unit.capacity} hóspedes` : '')
    add('Quartos', unit.bedroom_amount)
    add('Camas', unit.bed_amount)
    add('Casas de banho', unit.bathroom_amount)
    add('Estacionamento', unit.parking_spaces != null ? `${unit.parking_spaces} viaturas` : '')
    add('Check-in', unit.check_in_time || 'Sob consulta')
    add('Check-out', unit.check_out_time || 'Sob consulta')
  }
  return rows
})

const rentalConditions = computed(() => {
  const terms = item.value.terms || {}
  return [
    { label: 'Preço diário', value: formatPrice(terms.daily_price, terms.currency) },
    { label: 'Caução reembolsável', value: formatPrice(terms.deposit, terms.currency) },
    { label: 'Idade mínima', value: terms.minimum_age ? `${terms.minimum_age} anos` : 'Sob consulta' },
    { label: 'Carta de condução', value: terms.minimum_license_years ? `Mínimo ${terms.minimum_license_years} anos` : 'Sob consulta' },
    { label: 'Quilometragem', value: terms.included_km_per_day ? `${terms.included_km_per_day} km/dia` : 'Sob consulta' },
    { label: 'Política de combustível', value: terms.fuel_policy || 'Sob consulta' },
    { label: 'Seguro', value: terms.insurance || 'Sob consulta' },
    { label: 'Assistência', value: terms.assistance || 'Sob consulta' },
  ]
})

function formatPrice(value, currency = 'MZN') {
  if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) return 'Preço sob consulta'
  return new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: currency || 'MZN', maximumFractionDigits: 0 }).format(Number(value))
}
function serviceLabel(value) { return value === 'with_driver' ? 'Com motorista' : 'Sem motorista' }
function transmissionLabel(value) { return value === 'automatic' ? 'Automática' : value === 'manual' ? 'Manual' : value }
function fuelLabel(value) {
  const labels = { petrol: 'Gasolina', gasoline: 'Gasolina', diesel: 'Diesel', electric: 'Eléctrico', hybrid: 'Híbrido' }
  return labels[value] || value
}

async function load() {
  loading.value = true
  error.value = ''
  currentImage.value = 0
  relatedIndex.value = 0
  try {
    const endpoint = isRent.value ? `/rental-vehicles/slug/${route.params.slug}` : `/accommodations/slug/${route.params.slug}`
    const response = await apiClient.get(endpoint)
    item.value = response.data.data
    booking.option = units.value[0]?.id || ''
    booking.serviceType = serviceOptions.value[0]?.service_type || ''
    favorite.value = JSON.parse(localStorage.getItem('goldenkey:catalog-favorites') || '[]').includes(favoriteKey())
    await loadRelated()
    window.scrollTo(0, 0)
  } catch (requestError) {
    error.value = requestError.response?.status === 404 ? 'Registo não encontrado' : requestError.response?.data?.message || 'Não foi possível carregar este registo.'
  } finally {
    loading.value = false
  }
}

function unwrapCollection(data) {
  const payload = data?.data ?? data
  if (Array.isArray(payload)) return payload
  return Array.isArray(payload?.data) ? payload.data : []
}

function normalized(value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase() }
function relativeDifference(value, reference) {
  const first = Number(value)
  const second = Number(reference)
  if (!Number.isFinite(first) || !Number.isFinite(second) || second === 0) return Number.POSITIVE_INFINITY
  return Math.abs(first - second) / Math.abs(second)
}
function serviceTypes(candidate) { return new Set((candidate.service_options || []).filter(option => option.active !== false).map(option => option.service_type)) }
function candidatePrice(candidate) { return isRent.value ? candidate.terms?.daily_price ?? candidate.price : candidate.price ?? candidate.units?.[0]?.price }
function relatedScore(candidate) {
  let score = 0
  if (isRent.value) {
    if (normalized(candidate.category?.name) === normalized(item.value.category?.name)) score += 5
    if (normalized(candidate.location) === normalized(item.value.location)) score += 3
    if (normalized(candidate.transmission) === normalized(item.value.transmission)) score += 2
    if (normalized(candidate.fuel) === normalized(item.value.fuel)) score += 1
    if ([...serviceTypes(candidate)].some(type => serviceTypes(item.value).has(type))) score += 2
    if (Math.abs(Number(candidate.seats) - Number(item.value.seats)) <= 2) score += 1
  } else {
    if (normalized(candidate.type?.name) === normalized(item.value.type?.name)) score += 5
    if (normalized(candidate.location) === normalized(item.value.location)) score += 3
    if (Math.abs(Number(candidate.capacity) - Number(selectedUnit.value?.capacity)) <= 2) score += 2
    if (Math.abs(Number(candidate.bedroom_amount) - Number(selectedUnit.value?.bedroom_amount)) <= 1) score += 1
  }
  const priceDifference = relativeDifference(candidatePrice(candidate), candidatePrice(item.value))
  if (priceDifference <= 0.25) score += 2
  else if (priceDifference <= 0.5) score += 1
  return score
}
async function loadRelated() {
  relatedLoading.value = true
  try {
    const response = await apiClient.get(isRent.value ? '/rental-vehicles' : '/accommodations', { params: { per_page: 48 } })
    const seen = new Set()
    relatedItems.value = unwrapCollection(response.data)
      .filter(candidate => {
        if (!candidate?.slug || candidate.slug === item.value.slug || seen.has(candidate.slug)) return false
        seen.add(candidate.slug)
        return true
      })
      .map(candidate => ({ candidate, score: relatedScore(candidate) }))
      .sort((left, right) => right.score - left.score
        || Number(right.candidate.promotion?.priority || 0) - Number(left.candidate.promotion?.priority || 0)
        || relativeDifference(candidatePrice(left.candidate), candidatePrice(item.value)) - relativeDifference(candidatePrice(right.candidate), candidatePrice(item.value))
        || String(left.candidate.slug).localeCompare(String(right.candidate.slug)))
      .slice(0, 8).map(({ candidate }) => candidate)
  } catch {
    relatedItems.value = []
  } finally {
    relatedLoading.value = false
  }
}

function selectImage(index) { currentImage.value = index }
function previousImage() { currentImage.value = (currentImage.value - 1 + images.value.length) % images.value.length }
function nextImage() { currentImage.value = (currentImage.value + 1) % images.value.length }
function openGallery(index) { currentImage.value = index; galleryOpen.value = true }
function goBack() { window.history.state?.back ? router.back() : router.push(backPath.value) }
function openRelated(related) { router.push(`${backPath.value}/${related.slug}`) }
function relatedImage(related) {
  const candidate = related.primary_image_url || related.primary_image?.url || related.primary_image || related.images?.[0]?.url || related.images?.[0]
  return candidate || fallbackImage
}
function relatedPrice(related) { return formatPrice(candidatePrice(related), related.currency || related.terms?.currency || related.units?.[0]?.currency) }
function scrollRelated(direction) {
  const track = relatedTrack.value
  if (!track) return
  const firstCard = track.querySelector('article')
  const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 20
  const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth
  const visibleCards = window.innerWidth > 1100 ? 4 : window.innerWidth > 600 ? 2 : 1
  const lastIndex = Math.max(0, relatedItems.value.length - visibleCards)
  relatedIndex.value = Math.min(lastIndex, Math.max(0, relatedIndex.value + direction * visibleCards))
  track.scrollTo({ left: relatedIndex.value * (cardWidth + gap), behavior: 'smooth' })
}
function favoriteKey() { return `${props.kind}:${route.params.slug}` }
function toggleFavorite() {
  const favorites = new Set(JSON.parse(localStorage.getItem('goldenkey:catalog-favorites') || '[]'))
  favorite.value ? favorites.delete(favoriteKey()) : favorites.add(favoriteKey())
  favorite.value = !favorite.value
  localStorage.setItem('goldenkey:catalog-favorites', JSON.stringify([...favorites]))
}
watch(() => [route.params.slug, props.kind], load, { immediate: true })
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Karla:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

.service-detail-page { min-height: 100vh; padding: 16px 0 72px; color: #14110e; background: #fff; font-family: 'Karla', sans-serif; }
.detail-shell { box-sizing: border-box; width: min(100%, 1440px); margin-inline: auto; padding-inline: 48px; }
.breadcrumbs { display: flex; align-items: center; gap: 8px; min-height: 34px; overflow: hidden; color: #756e65; font-size: 12px; white-space: nowrap; }
.breadcrumbs button { padding: 0; border: 0; color: #7b6134; background: none; cursor: pointer; }
.breadcrumbs span:last-child { overflow: hidden; text-overflow: ellipsis; }

.gallery-section { display: grid; grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr); grid-template-rows: 502px; gap: 12px; margin-top: 8px; }
.gallery-section--single { grid-template-columns: 1fr; }
.gallery-main, .gallery-thumbnails button { position: relative; min-width: 0; padding: 0; overflow: hidden; border: 0; border-radius: 5px; background: #c9c2b6; cursor: pointer; }
.gallery-main > img, .gallery-thumbnails img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .35s ease; }
.gallery-main:hover > img, .gallery-thumbnails button:hover img { transform: scale(1.025); }
.gallery-thumbnails { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 12px; min-width: 0; }
.gallery-thumbnails--1 button { grid-column: 1 / -1; grid-row: 1 / -1; }
.gallery-thumbnails--2 button { grid-column: 1 / -1; }
.gallery-thumbnails--3 button:first-child { grid-row: 1 / -1; }
.gallery-thumbnails button.is-active { box-shadow: inset 0 0 0 3px #eba301; }
.gallery-badges { position: absolute; left: 14px; bottom: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
.gallery-badges span, .service-heading__badges span { padding: 6px 11px; border-radius: 999px; color: #fff; background: #14110e; font-size: 11px; line-height: 14px; font-weight: 600; }
.gallery-badges .promotion-badge, .service-heading__badges .promotion-badge { color: #14110e; background: #eba301; }
.gallery-arrows { position: absolute; inset: 0 14px; display: flex; align-items: center; justify-content: space-between; pointer-events: none; }
.gallery-arrows span { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 50%; color: #14110e; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.18); font-size: 27px; line-height: 1; pointer-events: auto; }
.gallery-more { position: absolute; right: 10px; bottom: 10px; padding: 7px 10px; border-radius: 5px; color: #fff; background: rgba(20,17,14,.8); font-size: 11px; font-weight: 600; }

.detail-content { display: grid; grid-template-columns: minmax(0, 972px) minmax(300px, 332px); gap: 40px; align-items: start; padding-top: 32px; }
.detail-primary { min-width: 0; display: grid; gap: 40px; }
.service-heading { padding-bottom: 24px; border-bottom: 1px solid #e6e0d6; }
.service-heading__badges { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.service-heading h1 { max-width: 820px; margin: 0; font-family: 'Sora', sans-serif; font-size: clamp(25px, 2.1vw, 34px); line-height: 1.22; letter-spacing: -.02em; }
.service-location { display: flex; align-items: center; gap: 5px; margin: 10px 0 18px; color: #5a5147; font-size: 14px; }
.service-price { margin: 0; font-family: 'Sora', sans-serif; font-size: 30px; line-height: 1.2; font-weight: 700; }
.service-price small, .option-price small { margin-left: 5px; color: #5a5147; font: 400 12px/16px 'Karla', sans-serif; }
.detail-block { min-width: 0; }
.detail-block h2 { margin: 0 0 18px; font-family: 'Sora', sans-serif; font-size: 20px; line-height: 26px; font-weight: 700; }
.characteristics-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0; border-top: 1px solid #e6e0d6; }
.characteristics-grid > div { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; padding: 13px 0; border-bottom: 1px solid #e6e0d6; }
.characteristics-grid > div:nth-child(odd) { padding-right: 28px; }
.characteristics-grid > div:nth-child(even) { padding-left: 28px; border-left: 1px solid #e6e0d6; }
.characteristics-grid dt { color: #756e65; font-size: 13px; }
.characteristics-grid dd { margin: 0; text-align: right; overflow-wrap: anywhere; font-size: 13px; font-weight: 600; }

.section-heading { display: flex; align-items: end; justify-content: space-between; gap: 20px; margin-bottom: 18px; }
.section-heading span, .partner-section > span { color: #a87300; font-size: 10px; line-height: 13px; font-weight: 700; letter-spacing: .13em; }
.section-heading h2 { margin: 5px 0 0; }
.section-heading > small { color: #756e65; font-size: 12px; }
.option-grid { display: grid; gap: 10px; }
.option-grid button { position: relative; min-width: 0; display: grid; grid-template-columns: 42px minmax(0, 1fr) auto 20px; gap: 12px; align-items: center; padding: 14px; border: 1px solid #e6e0d6; border-radius: 5px; color: #14110e; background: #fff; text-align: left; cursor: pointer; transition: border-color .18s, background .18s, box-shadow .18s; }
.option-grid button:hover { border-color: #c7bda9; }
.option-grid button.is-selected { border-color: #eba301; background: #fffbef; box-shadow: 0 0 0 1px #eba301; }
.option-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 50%; color: #7b6134; background: #f4f0e8; }
.option-copy { min-width: 0; display: grid; gap: 3px; }
.option-copy strong { overflow: hidden; font-family: 'Sora', sans-serif; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.option-copy small { color: #756e65; font-size: 11px; }
.option-price { text-align: right; font-size: 13px; font-weight: 700; }
.option-price small { display: block; margin: 2px 0 0; }
.option-check { color: #eba301; opacity: 0; }
.is-selected .option-check { opacity: 1; }
.option-grid--services { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.option-grid--services button { grid-template-columns: 42px minmax(0, 1fr); align-content: start; }
.option-grid--services .option-price { grid-column: 2; text-align: left; }
.option-grid--services .option-check { position: absolute; top: 13px; right: 13px; }

.amenities-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.amenities-grid > div { display: flex; align-items: center; gap: 9px; min-height: 44px; padding: 0 13px; border: 1px solid #e6e0d6; border-radius: 5px; color: #5a5147; font-size: 12px; }
.amenities-grid .q-icon { color: #a87300; }
.description-section p { margin: 0; color: #5a5147; font-size: 14px; line-height: 1.7; white-space: pre-line; }
.location-placeholder { box-sizing: border-box; min-height: 230px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px; color: #5a5147; background: #f4f0e8; text-align: center; }
.location-placeholder strong { color: #14110e; font-size: 16px; }
.location-placeholder span { max-width: 420px; font-size: 12px; }

.detail-sidebar { position: sticky; top: 20px; min-width: 0; display: grid; gap: 18px; }
.contact-card { padding: 20px; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); }
.agent-row { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.agent-row img { width: 54px; height: 54px; flex: 0 0 auto; border: 1px solid #eee8de; border-radius: 50%; object-fit: cover; }
.agent-row > div { min-width: 0; display: grid; gap: 2px; }
.agent-row span { color: #756e65; font-size: 10px; }
.agent-row strong, .agent-row small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agent-row strong { font-family: 'Sora', sans-serif; font-size: 14px; }
.agent-row small { color: #756e65; font-size: 11px; }
.contact-button { width: 100%; min-height: 42px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: 10px; padding: 0 12px; border: 1px solid #a87300; border-radius: 5px; color: #7b5200; background: #fff; text-decoration: none; font: 600 12px/16px 'Karla', sans-serif; cursor: pointer; }
.contact-button--primary { border-color: #eba301; color: #14110e; background: #eba301; font-weight: 700; }
.contact-button--save { border-color: #e6e0d6; color: #14110e; }
.contact-meta { display: grid; gap: 9px; margin: 18px 0 0; padding-top: 16px; border-top: 1px solid #e6e0d6; }
.contact-meta > div { display: flex; justify-content: space-between; gap: 12px; }
.contact-meta dt { color: #756e65; font-size: 11px; }
.contact-meta dd { margin: 0; font-size: 11px; font-weight: 600; text-align: right; }
.partner-section { padding: 18px; border: 1px solid #e6e0d6; border-radius: 5px; background: #f4f0e8; }
.partner-section h2 { margin: 6px 0 14px; font-family: 'Sora', sans-serif; font-size: 16px; line-height: 22px; }
.detail-partner-ad { aspect-ratio: 3 / 2 !important; min-height: 0 !important; border-radius: 3px; }

.related-section { margin-top: 58px; }
.related-heading, .related-heading__actions, .related-slider-controls { display: flex; align-items: center; }
.related-heading { justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.related-heading h2 { margin: 0; font: 700 22px/29px 'Sora', sans-serif; }
.related-heading__actions { gap: 12px; }
.related-slider-controls { gap: 7px; }
.related-slider-controls button { width: 38px; height: 38px; display: grid; place-items: center; padding: 0; border: 1px solid #e0d6c6; border-radius: 50%; color: #7b5200; background: #fff; cursor: pointer; font-size: 18px; }
.related-slider-controls button:hover { border-color: #eba301; background: #fffbef; }
.related-see-all { min-height: 38px; padding: 0 14px; border: 1px solid #a87300; border-radius: 5px; color: #7b5200; background: #fff; cursor: pointer; font: 600 12px/16px 'Karla', sans-serif; }
.related-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; }
.related-grid--carousel { grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: calc((100% - 60px) / 4); overflow-x: hidden; scroll-snap-type: x mandatory; }
.related-grid article { min-width: 0; overflow: hidden; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; cursor: pointer; scroll-snap-align: start; transition: transform .2s, box-shadow .2s; }
.related-grid article:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(20,17,14,.1); }
.related-image { position: relative; aspect-ratio: 4 / 3; overflow: hidden; background: #eee8de; }
.related-image img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .35s ease; }
.related-grid article:hover .related-image img { transform: scale(1.035); }
.related-image > span { position: absolute; top: 10px; left: 10px; max-width: calc(100% - 20px); padding: 5px 8px; overflow: hidden; border-radius: 999px; color: #fff; background: #14110e; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.related-content { padding: 14px; }
.related-content > strong { display: block; font: 700 17px/22px 'Sora', sans-serif; }
.related-content > strong small { margin-left: 3px; color: #756e65; font: 400 10px/14px 'Karla', sans-serif; }
.related-content h3 { margin: 12px 0 4px; overflow: hidden; font: 600 14px/19px 'Sora', sans-serif; text-overflow: ellipsis; white-space: nowrap; }
.related-content p { margin: 0; overflow: hidden; color: #756e65; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.related-facts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.related-facts span { padding: 5px 7px; border-radius: 3px; color: #5a5147; background: #f4f0e8; font-size: 10px; }
.related-grid--loading > div { min-height: 330px; border-radius: 5px; background: linear-gradient(90deg, #e6e0d6, #f4f0e8, #e6e0d6); background-size: 200% 100%; animation: shimmer 1.5s linear infinite; }

.detail-loading { padding-top: 36px; }
.loading-gallery, .loading-panel { background: linear-gradient(90deg, #e6e0d6, #f4f0e8, #e6e0d6); background-size: 200% 100%; animation: shimmer 1.5s linear infinite; }
.loading-gallery { height: 502px; border-radius: 5px; }
.loading-grid { display: grid; grid-template-columns: minmax(0, 972px) minmax(300px, 332px); gap: 40px; margin-top: 32px; }
.loading-panel { height: 660px; border-radius: 5px; }
.loading-panel--side { height: 450px; }
.detail-error { min-height: 520px; display: grid; place-content: center; justify-items: center; gap: 9px; color: #5a5147; text-align: center; }
.detail-error h1 { margin: 4px 0 0; color: #14110e; font: 700 22px/29px 'Sora', sans-serif; }
.detail-error p { margin: 0 0 8px; }
.detail-error button { min-height: 42px; padding: 0 18px; border: 0; border-radius: 5px; color: #14110e; background: #eba301; font-weight: 700; cursor: pointer; }
.gallery-dialog__content { height: calc(100vh - 50px); display: grid; place-items: center; }
.gallery-carousel { width: min(92vw, 1400px); height: 84vh; }
.gallery-carousel :deep(.q-carousel__slide) { background-size: contain; background-repeat: no-repeat; }
@keyframes shimmer { to { background-position: -200% 0; } }

@media (max-width: 1100px) {
  .detail-shell { padding-inline: 24px; }
  .gallery-section { grid-template-rows: 420px; }
  .detail-content { grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; }
  .amenities-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .related-grid--carousel { grid-auto-columns: calc((100% - 20px) / 2); }
}
@media (max-width: 820px) {
  .gallery-section { grid-template-columns: 1fr; grid-template-rows: minmax(300px, 58vw) 150px; }
  .gallery-section--single { grid-template-rows: minmax(300px, 58vw); }
  .gallery-thumbnails { grid-template-columns: repeat(4, minmax(0, 1fr)); grid-template-rows: 1fr; }
  .gallery-thumbnails--1 { grid-template-columns: 1fr; }
  .gallery-thumbnails--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gallery-thumbnails--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .gallery-thumbnails--1 button, .gallery-thumbnails--2 button, .gallery-thumbnails--3 button:first-child { grid-column: auto; grid-row: auto; }
  .detail-content, .loading-grid { grid-template-columns: 1fr; }
  .detail-primary { display: contents; }
  .service-heading { grid-row: 1; }
  .detail-sidebar { position: static; grid-row: 2; }
  .characteristics-section { grid-row: 3; }
  .option-section { grid-row: 4; }
  .amenities-section, .conditions-section { grid-row: 5; }
  .description-section { grid-row: 6; }
  .location-section { grid-row: 7; }
  .partner-section { display: block; }
}
@media (max-width: 600px) {
  .service-detail-page { padding-top: 8px; padding-bottom: 48px; }
  .detail-shell { padding-inline: 16px; }
  .breadcrumbs { min-height: 38px; }
  .gallery-section { grid-template-rows: 64vw 82px; gap: 8px; margin-top: 2px; }
  .gallery-section--single { grid-template-rows: 64vw; }
  .gallery-thumbnails { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
  .gallery-thumbnails--1 { grid-template-columns: 1fr; }
  .gallery-thumbnails--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gallery-thumbnails--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .gallery-thumbnails button:nth-child(n+4) { display: none; }
  .gallery-badges { left: 10px; bottom: 10px; }
  .gallery-arrows { display: none; }
  .related-section { margin-top: 40px; }
  .related-heading { align-items: flex-start; }
  .related-heading h2 { font-size: 18px; line-height: 24px; }
  .related-see-all { display: none; }
  .related-grid--carousel { grid-auto-columns: 86%; overflow-x: auto; }
  .detail-content { gap: 28px; padding-top: 24px; }
  .detail-primary { gap: 32px; }
  .service-heading h1 { font-size: 23px; }
  .service-price { font-size: 25px; }
  .characteristics-grid { grid-template-columns: 1fr; }
  .characteristics-grid > div:nth-child(odd), .characteristics-grid > div:nth-child(even) { padding: 12px 0; border-left: 0; }
  .characteristics-grid > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
  .characteristics-grid dt { min-width: 0; }
  .characteristics-grid dd { min-width: 0; max-width: 58%; flex: 0 1 58%; white-space: normal; word-break: break-word; }
  .booking-fields { grid-template-columns: 1fr; }
  .booking-fields label, .booking-fields label:nth-child(2), .booking-fields .booking-field--wide { grid-column: auto; border-right: 0; }
  .option-grid--services { grid-template-columns: 1fr; }
  .option-grid button { grid-template-columns: 38px minmax(0, 1fr) 20px; }
  .option-grid .option-price { grid-column: 2; text-align: left; }
  .option-grid .option-check { position: absolute; top: 13px; right: 13px; }
  .amenities-grid { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .loading-gallery, .loading-panel { animation: none; }
  .gallery-main > img, .gallery-thumbnails img { transition: none; }
}
</style>
